// client/src/components/common/FooterBoardCard.jsx
//
// A call-board call in the docked desktop footer (receptionists / managers).
// Same 300×44 card as FooterCallCard; Transfer / End go through /api/call-board
// because the call is usually on someone else's phone.
import { useState } from 'react';
import { PhoneOff, ArrowRightCircle, AlertTriangle, Loader2 } from 'lucide-react';
import useBoardCallActions from '../../hooks/useBoardCallActions';
import { boardWhoLine, useElapsed } from '../../hooks/useBoardPresentation';
import { boardCallActionable } from '../../utils/callVisibility';
import { openCallerScreen } from '../../utils/openCallerScreen';

const BG = {
  Ringing: 'bg-[#ef4444]',
  Connected: 'bg-[#10b981]',
  'On Hold': 'bg-amber-600',
  Transferring: 'bg-blue-600',
};

export default function FooterBoardCard({ call, formatPhoneNumber, allExtensions }) {
  const [showTransfer, setShowTransfer] = useState(false);
  const [destination, setDestination] = useState('');
  const { busy, result, transfer, end } = useBoardCallActions(call.callId);

  const isRinging = call.status === 'Ringing';
  const elapsed = useElapsed(isRinging ? call.firstSeen : (call.connectedAt || call.firstSeen));
  const actionable = boardCallActionable(call);
  const name = call.callerName || formatPhoneNumber(call.callerNumber);
  const subline = boardWhoLine(call) || formatPhoneNumber(call.callerNumber);
  const dept = call.ringGroupName || null;
  const time = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  const submit = async () => {
    if (await transfer(destination)) {
      setDestination('');
      setTimeout(() => setShowTransfer(false), 1500);
    }
  };

  return (
    <div className={`flex-shrink-0 w-[300px] h-[44px] rounded-md flex items-center px-3 transition-all ${BG[call.status] || 'bg-[#2d3748]'} border border-white/10 shadow-md`}>
      <div className="flex-1 min-w-0 pr-2">
        <div className="flex items-center gap-1.5 mb-0.5">
          <button
            type="button"
            onClick={() => openCallerScreen({
              callId: call.callId,
              callerNumber: call.callerNumber,
              extension: call.answeredBy || call.legs?.[0]?.ext,
            })}
            className="min-w-0 text-left text-white font-bold text-[13px] truncate leading-none hover:underline focus:outline-none focus-visible:underline"
            title={`Open ${name}`}
          >
            {name}
          </button>
          {dept && (
            <span className="flex-shrink-0 rounded bg-black/25 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-white/90">{dept}</span>
          )}
          {isRinging && <AlertTriangle className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />}
        </div>
        {result ? (
          <p className={`text-[10px] font-bold leading-none truncate ${result.success ? 'text-emerald-100' : 'text-red-100'}`} title={result.message}>
            {result.message}
          </p>
        ) : (
          <p className="text-white text-[11px] opacity-80 truncate leading-none" title={subline}>{subline}</p>
        )}
      </div>

      <div className="flex items-center gap-2 border-l border-white/20 pl-2 flex-shrink-0">
        {showTransfer ? (
          <div className="flex items-center gap-1.5">
            <input
              autoFocus
              type="text"
              list={`board-card-ext-${call.callId}`}
              placeholder="Ext..."
              aria-label="Transfer to"
              className="w-16 bg-white/10 border-none outline-none text-white text-[11px] px-1.5 py-1 rounded placeholder:text-white/40"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') setShowTransfer(false);
              }}
            />
            <datalist id={`board-card-ext-${call.callId}`}>
              {allExtensions?.map((ext) => (
                <option key={ext.Number} value={ext.Number}>{ext.FirstName} {ext.LastName}</option>
              ))}
            </datalist>
            <button onClick={submit} disabled={busy === 'transfer'} aria-label="Transfer call" className="p-1 bg-blue-500 rounded text-white hover:bg-blue-600">
              {busy === 'transfer' ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRightCircle className="w-3 h-3" />}
            </button>
            <button onClick={() => setShowTransfer(false)} aria-label="Cancel transfer" className="text-[10px] text-white/50 hover:text-white px-1">✕</button>
          </div>
        ) : (
          <>
            <div className="text-right min-w-[40px]">
              <p className="text-white text-[9px] font-bold opacity-70 uppercase tracking-tighter leading-none mb-0.5">{call.status}</p>
              <p className="text-white text-[13px] font-black leading-none font-mono">{time}</p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={end}
                disabled={!actionable || busy === 'end'}
                aria-label="End call"
                title={actionable ? 'End Call' : 'Available once someone answers'}
                className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${!actionable || busy === 'end' ? 'bg-gray-500/50 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {busy === 'end' ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" /> : <PhoneOff className="w-3.5 h-3.5 text-white" />}
              </button>
              {actionable && (
                <button
                  onClick={() => setShowTransfer(true)}
                  aria-label="Transfer Call"
                  title="Transfer Call"
                  className="w-7 h-7 bg-blue-500 hover:bg-blue-600 rounded flex items-center justify-center transition-colors shadow-sm"
                >
                  <ArrowRightCircle className="w-3.5 h-3.5 text-white" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
